import { ReactElement, useCallback, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import dayjs from 'dayjs';

import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import { CoursePair } from '../../types/Course';
import { PARTICIPANT_ORDERS, ParticipantOrder } from '../../types/Event';
import { TimeOfDay } from '../../types/Org';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import CourseSelector from '../course/CourseSelector';
import EventPriorityEditor from '../event/EventPriorityEditor';

import GratiDaysField from './GratiDaysField';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface DefaultEventSettings {
  defaultPlayDays?: number[];
  defaultEventSignupOpensDaysPrior?: number;
  defaultEventSignupClosesDaysPrior?: number;
  defaultCoursePair?: CoursePair;
  defaultStartTime?: TimeOfDay;
  defaultMaxPlayers?: number;
  defaultNumTeamsInMatch?: number;
  defaultSignupPriority?: ParticipantOrder;
  defaultGhinIndexSetDaysPrior?: number;
  defaultTeamSize?: number;
  defaultTeeSheetGroupSize?: number;
  defaultShowRosterToAllDaysPrior?: number;
  defaultShowRosterToTeamDaysPrior?: number;
}

interface DefaultEventSettingsEditorProps {
  /** Current settings values */
  settings: DefaultEventSettings;
  /** Called when any setting changes */
  onChange: <K extends keyof DefaultEventSettings>(field: K, value: DefaultEventSettings[K]) => void;
  /** Parent settings to show as placeholders (for league inheriting from org) */
  parentSettings?: DefaultEventSettings;
  /** Whether this is at the org level (no inheritance placeholders) */
  isOrgLevel?: boolean;
  /** Whether this is a team league (shows per-team limit and Manager priority option) */
  isTeamLeague?: boolean;
  /** League-only: current default event team size (players per team per event) */
  maxPlayersPerTeam?: number;
  /** League-only: inherited default event team size (for placeholder) */
  parentMaxPlayersPerTeam?: number;
  /** League-only: called when default event team size changes */
  onMaxPlayersPerTeamChange?: (value: number | undefined) => void;
}

function totalDaysToWeeksDays(totalDays: number | undefined, defaultWeeks = '', defaultDays = ''): { weeks: string; days: string } {
  if (totalDays === undefined || totalDays < 0) {
    return { weeks: defaultWeeks, days: defaultDays };
  }
  return {
    weeks: String(Math.floor(totalDays / 7)),
    days: String(totalDays % 7),
  };
}

function weeksDaysToTotalDays(weeks: string, days: string): number | undefined {
  const w = weeks === '' ? 0 : parseInt(weeks);
  const d = days === '' ? 0 : parseInt(days);
  if (isNaN(w) || isNaN(d)) return undefined;
  if (weeks === '' && days === '') return undefined;
  return w * 7 + d;
}

export default function DefaultEventSettingsEditor(props: DefaultEventSettingsEditorProps): ReactElement {
  const { settings, onChange, parentSettings, isOrgLevel = false, isTeamLeague = false, maxPlayersPerTeam, parentMaxPlayersPerTeam, onMaxPlayersPerTeamChange } = props;

  const isDayOfEvent = settings.defaultGhinIndexSetDaysPrior === -1;

  // Local state for GHIN week/day inputs
  const [ghinLocal, setGhinLocal] = useState(() =>
    totalDaysToWeeksDays(settings.defaultGhinIndexSetDaysPrior, isOrgLevel ? '0' : '', isOrgLevel ? '2' : '')
  );

  // Local state for roster visibility week/day inputs
  const [rosterAllLocal, setRosterAllLocal] = useState(() =>
    totalDaysToWeeksDays(settings.defaultShowRosterToAllDaysPrior)
  );
  const [rosterTeamLocal, setRosterTeamLocal] = useState(() =>
    totalDaysToWeeksDays(settings.defaultShowRosterToTeamDaysPrior)
  );

  // Sync GHIN local state when prop value changes externally
  useEffect(() => {
    const newWeeksDays = totalDaysToWeeksDays(settings.defaultGhinIndexSetDaysPrior, isOrgLevel ? '0' : '', isOrgLevel ? '2' : '');
    setGhinLocal((prev) => {
      const prevTotal = weeksDaysToTotalDays(prev.weeks, prev.days);
      if (prevTotal !== settings.defaultGhinIndexSetDaysPrior) {
        return newWeeksDays;
      }
      return prev;
    });
  }, [settings.defaultGhinIndexSetDaysPrior, isOrgLevel]);

  // Sync roster local states when prop values change externally
  useEffect(() => {
    setRosterAllLocal((prev) => {
      const prevTotal = weeksDaysToTotalDays(prev.weeks, prev.days);
      if (prevTotal !== settings.defaultShowRosterToAllDaysPrior) {
        return totalDaysToWeeksDays(settings.defaultShowRosterToAllDaysPrior);
      }
      return prev;
    });
  }, [settings.defaultShowRosterToAllDaysPrior]);

  useEffect(() => {
    setRosterTeamLocal((prev) => {
      const prevTotal = weeksDaysToTotalDays(prev.weeks, prev.days);
      if (prevTotal !== settings.defaultShowRosterToTeamDaysPrior) {
        return totalDaysToWeeksDays(settings.defaultShowRosterToTeamDaysPrior);
      }
      return prev;
    });
  }, [settings.defaultShowRosterToTeamDaysPrior]);

  const handleGhinWeeksChange = useCallback((value: string) => {
    setGhinLocal((prev) => ({ ...prev, weeks: value }));
  }, []);

  const handleGhinDaysChange = useCallback((value: string) => {
    const num = parseInt(value);
    if (value !== '' && !isNaN(num)) {
      const clamped = Math.max(0, Math.min(6, num));
      setGhinLocal((prev) => ({ ...prev, days: String(clamped) }));
    } else {
      setGhinLocal((prev) => ({ ...prev, days: value }));
    }
  }, []);

  const handleGhinBlur = useCallback(() => {
    const newTotal = weeksDaysToTotalDays(ghinLocal.weeks, ghinLocal.days);
    onChange('defaultGhinIndexSetDaysPrior', newTotal);
  }, [ghinLocal, onChange]);

  const makeDaysInputs = (
    local: { weeks: string; days: string },
    onWeeksChange: (v: string) => void,
    onDaysChange: (v: string) => void,
    onBlur: () => void
  ) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <TextField
        type="number"
        size="small"
        value={local.weeks}
        onChange={(e) => onWeeksChange(e.target.value)}
        onBlur={onBlur}
        slotProps={{
          htmlInput: { min: 0, style: { textAlign: 'right' } },
          input: { endAdornment: <InputAdornment position="end">w</InputAdornment> },
        }}
        sx={{ width: '5em' }}
      />
      <TextField
        type="number"
        size="small"
        value={local.days}
        onChange={(e) => onDaysChange(e.target.value)}
        onBlur={onBlur}
        slotProps={{
          htmlInput: { min: 0, max: 6, style: { textAlign: 'right' } },
          input: { endAdornment: <InputAdornment position="end">d</InputAdornment> },
        }}
        sx={{ width: '4.5em' }}
      />
    </Box>
  );

  // Sensible default when switching a roster field to "Days prior"
  const rosterDaysPriorDefault = (): number => {
    const ghin = settings.defaultGhinIndexSetDaysPrior;
    return (ghin !== undefined && ghin >= 0) ? ghin : (isOrgLevel ? 2 : 0);
  };

  const getPlaceholder = (field: keyof DefaultEventSettings, defaultValue: string): string => {
    if (isOrgLevel) return defaultValue;
    const parentValue = parentSettings?.[field];
    if (parentValue === undefined || parentValue === null) return defaultValue;
    if (typeof parentValue === 'number') return parentValue.toString();
    if (typeof parentValue === 'object' && 'hour' in parentValue) {
      return `${parentValue.hour}:${parentValue.minute.toString().padStart(2, '0')}`;
    }
    return defaultValue;
  };

  return (
    <Stack spacing={2}>
      {/* Default Play Days */}
      <Box>
        <Typography variant="bodyMedium" sx={{ mb: 1 }}>
          Default play days
        </Typography>
        <Stack direction="row" spacing={0.5} useFlexGap sx={{
          flexWrap: "wrap"
        }}>
          {DAYS_OF_WEEK.map((day, index) => (
            <Chip
              key={day}
              label={day}
              size="small"
              color={settings.defaultPlayDays?.includes(index) ? 'primary' : 'default'}
              variant={settings.defaultPlayDays?.includes(index) ? 'filled' : 'outlined'}
              onClick={() => {
                const currentDays = settings.defaultPlayDays ?? [];
                const newDays = currentDays.includes(index)
                  ? currentDays.filter((d) => d !== index)
                  : [...currentDays, index].sort((a, b) => a - b);
                onChange('defaultPlayDays', newDays.length > 0 ? newDays : undefined);
              }}
            />
          ))}
        </Stack>
      </Box>
      <Divider />
      {/* Registration timing */}
      <GratiDaysField
        opensValue={settings.defaultEventSignupOpensDaysPrior}
        closesValue={settings.defaultEventSignupClosesDaysPrior}
        onOpensChange={(value) => onChange('defaultEventSignupOpensDaysPrior', value)}
        onClosesChange={(value) => onChange('defaultEventSignupClosesDaysPrior', value)}
        opensPlaceholder={getPlaceholder('defaultEventSignupOpensDaysPrior', '6')}
        closesPlaceholder={getPlaceholder('defaultEventSignupClosesDaysPrior', '2')}
        showPlaceholders={!isOrgLevel}
      />
      <Divider />
      {/* Event settings */}
      <Typography variant="labelMedium" sx={{
        color: "text.secondary"
      }}>
        Default event settings
      </Typography>
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <CourseSelector
          value={settings.defaultCoursePair?.id ?? ''}
          handleChange={(course) => {
            onChange('defaultCoursePair', {
              id: course.itemId,
              name: course.name,
              shortName: course.shortName ?? course.name,
              timezone: course.timezone,
            });
          }}
          sx={{ maxWidth: '20em' }}
        />
        <TimePicker
          label="First tee time"
          value={
            settings.defaultStartTime
              ? dayjs().hour(settings.defaultStartTime.hour).minute(settings.defaultStartTime.minute)
              : isOrgLevel
                ? dayjs().hour(9).minute(0)
                : null
          }
          onChange={(time) => {
            if (time) {
              onChange('defaultStartTime', { hour: time.hour(), minute: time.minute() });
            } else {
              onChange('defaultStartTime', undefined);
            }
          }}
          sx={{ maxWidth: '8em' }}
        />
      </Stack>
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        {settings.defaultMaxPlayers !== -1 && (
          <TextField
            label="Capacity"
            type="number"
            value={settings.defaultMaxPlayers ?? ''}
            placeholder={getPlaceholder('defaultMaxPlayers', '24')}
            onChange={({ target }) => {
              const value = target.value === '' ? undefined : parseInt(target.value);
              onChange('defaultMaxPlayers', value === undefined ? undefined : Math.max(1, value));
            }}
            slotProps={{ htmlInput: { min: 1 } }}
            sx={{ width: '8em' }}
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={settings.defaultMaxPlayers === -1}
              onChange={(e) => {
                onChange('defaultMaxPlayers', e.target.checked ? -1 : isOrgLevel ? 24 : undefined);
              }}
            />
          }
          label={settings.defaultMaxPlayers === -1 ? 'Unlimited capacity' : 'Unlimited'}
        />
      </Stack>
      <TextField
        select
        label="Default teams per match"
        size="small"
        sx={{ minWidth: '8em' }}
        value={settings.defaultNumTeamsInMatch ?? 0}
        onChange={(e) => {
          const value = Number(e.target.value);
          onChange('defaultNumTeamsInMatch', value === 0 ? undefined : value);
        }}
      >
        <MenuItem value={0}>
          <em>{parentSettings?.defaultNumTeamsInMatch ? `${parentSettings.defaultNumTeamsInMatch} (inherited)` : 'None'}</em>
        </MenuItem>
        {Array.from({ length: 7 }, (_, i) => i + 2).map((size) => (
          <MenuItem key={size} value={size}>{size} teams</MenuItem>
        ))}
      </TextField>
      {isTeamLeague && (
        <TextField
          select
          label="Default event team size"
          size="small"
          sx={{ minWidth: '8em' }}
          value={maxPlayersPerTeam ?? 0}
          onChange={(e) => {
            const value = Number(e.target.value);
            onMaxPlayersPerTeamChange?.(value === 0 ? undefined : value);
          }}
        >
          <MenuItem value={0}>
            <em>{parentMaxPlayersPerTeam ? `${parentMaxPlayersPerTeam} (inherited)` : 'None'}</em>
          </MenuItem>
          {Array.from({ length: 19 }, (_, i) => i + 2).map((size) => (
            <MenuItem key={size} value={size}>{size} players</MenuItem>
          ))}
        </TextField>
      )}
      <TextField
        select
        label="Default roster size"
        size="small"
        sx={{ minWidth: '8em' }}
        value={settings.defaultTeamSize ?? 0}
        onChange={(e) => {
          const value = Number(e.target.value);
          onChange('defaultTeamSize', value === 0 ? undefined : value);
        }}
      >
        <MenuItem value={0}>
          <em>{parentSettings?.defaultTeamSize ? `${parentSettings.defaultTeamSize} (inherited)` : 'None'}</em>
        </MenuItem>
        {Array.from({ length: 19 }, (_, i) => i + 2).map((size) => (
          <MenuItem key={size} value={size}>{size} players</MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Default tee sheet group size"
        size="small"
        sx={{ minWidth: '8em' }}
        value={settings.defaultTeeSheetGroupSize ?? 0}
        onChange={(e) => {
          const value = Number(e.target.value);
          onChange('defaultTeeSheetGroupSize', value === 0 ? undefined : value);
        }}
      >
        <MenuItem value={0}>
          <em>{parentSettings?.defaultTeeSheetGroupSize ? `${parentSettings.defaultTeeSheetGroupSize} (inherited)` : '4 (default)'}</em>
        </MenuItem>
        {[3, 4].map((size) => (
          <MenuItem key={size} value={size}>{size} per group</MenuItem>
        ))}
      </TextField>
      <EventPriorityEditor
        value={settings.defaultSignupPriority}
        onChange={(value) => onChange('defaultSignupPriority', value)}
        showDescription
        fallbackValue={isOrgLevel ? PARTICIPANT_ORDERS.SIGNUP_TIME : parentSettings?.defaultSignupPriority}
        allowClear={!isOrgLevel}
        showMgrChoice={isTeamLeague}
      />
      {/* Handicap Index lock timing */}
      <Stack spacing={0.5}>
        <Typography variant="labelMedium" sx={{ color: 'text.secondary' }}>
          {HANDICAP_LABELS.INDEX} locks:
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 1 }} sx={{ alignItems: { xs: 'flex-start', md: 'center' } }}>
          <ToggleButtonGroup
            value={isDayOfEvent ? 'day-of' : 'prior'}
            exclusive
            size="small"
            onChange={(_, val) => {
              if (val === 'day-of') {
                onChange('defaultGhinIndexSetDaysPrior', -1);
              } else if (val === 'prior') {
                const defaultValue = isOrgLevel ? 2 : undefined;
                onChange('defaultGhinIndexSetDaysPrior', defaultValue);
                setGhinLocal(totalDaysToWeeksDays(defaultValue, isOrgLevel ? '0' : '', isOrgLevel ? '2' : ''));
              }
            }}
          >
            <ToggleButton value="day-of">Day of event</ToggleButton>
            <ToggleButton value="prior">Days prior</ToggleButton>
          </ToggleButtonGroup>
          {!isDayOfEvent && makeDaysInputs(ghinLocal, handleGhinWeeksChange, handleGhinDaysChange, handleGhinBlur)}
        </Stack>
      </Stack>
      {/* Roster visibility for all */}
      <Stack spacing={0.5}>
        <Typography variant="labelMedium" sx={{ color: 'text.secondary' }}>
          Roster visible to all:
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 1 }} sx={{ alignItems: { xs: 'flex-start', md: 'center' } }}>
          <ToggleButtonGroup
            value={settings.defaultShowRosterToAllDaysPrior !== undefined ? 'prior' : 'always'}
            exclusive
            size="small"
            onChange={(_, val) => {
              if (val === 'always') {
                onChange('defaultShowRosterToAllDaysPrior', undefined);
              } else if (val === 'prior') {
                const defaultValue = rosterDaysPriorDefault();
                onChange('defaultShowRosterToAllDaysPrior', defaultValue);
                setRosterAllLocal(totalDaysToWeeksDays(defaultValue));
              }
            }}
          >
            <ToggleButton value="always">Always</ToggleButton>
            <ToggleButton value="prior">Days prior</ToggleButton>
          </ToggleButtonGroup>
          {settings.defaultShowRosterToAllDaysPrior !== undefined && makeDaysInputs(
            rosterAllLocal,
            (v) => setRosterAllLocal((prev) => ({ ...prev, weeks: v })),
            (v) => {
              const num = parseInt(v);
              setRosterAllLocal((prev) => ({
                ...prev,
                days: v !== '' && !isNaN(num) ? String(Math.max(0, Math.min(6, num))) : v,
              }));
            },
            () => onChange('defaultShowRosterToAllDaysPrior', weeksDaysToTotalDays(rosterAllLocal.weeks, rosterAllLocal.days))
          )}
        </Stack>
      </Stack>
      {/* Roster visibility for team members (team league only) */}
      {isTeamLeague && (
        <Stack spacing={0.5}>
          <Typography variant="labelMedium" sx={{ color: 'text.secondary' }}>
            Team visible to members:
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 1 }} sx={{ alignItems: { xs: 'flex-start', md: 'center' } }}>
            <ToggleButtonGroup
              value={settings.defaultShowRosterToTeamDaysPrior !== undefined ? 'prior' : 'always'}
              exclusive
              size="small"
              onChange={(_, val) => {
                if (val === 'always') {
                  onChange('defaultShowRosterToTeamDaysPrior', undefined);
                } else if (val === 'prior') {
                  const defaultValue = rosterDaysPriorDefault();
                  onChange('defaultShowRosterToTeamDaysPrior', defaultValue);
                  setRosterTeamLocal(totalDaysToWeeksDays(defaultValue));
                }
              }}
            >
              <ToggleButton value="always">Always</ToggleButton>
              <ToggleButton value="prior">Days prior</ToggleButton>
            </ToggleButtonGroup>
            {settings.defaultShowRosterToTeamDaysPrior !== undefined && makeDaysInputs(
              rosterTeamLocal,
              (v) => setRosterTeamLocal((prev) => ({ ...prev, weeks: v })),
              (v) => {
                const num = parseInt(v);
                setRosterTeamLocal((prev) => ({
                  ...prev,
                  days: v !== '' && !isNaN(num) ? String(Math.max(0, Math.min(6, num))) : v,
                }));
              },
              () => onChange('defaultShowRosterToTeamDaysPrior', weeksDaysToTotalDays(rosterTeamLocal.weeks, rosterTeamLocal.days))
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
